import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import Icon from '@minecraft.icon';

type PillSize = 'lg' | 'sm';

const SIZE_CONFIGS: Record<PillSize, { height: string; padding: string; fontSize: string }> = {
  sm: {
    height: '1.5rem',
    padding: '0.188rem 0.8rem',
    fontSize: '0.75rem',
  },
  lg: {
    height: '2rem',
    padding: '0.188rem 1rem',
    fontSize: '0.875rem',
  },
};

type PillVariant = 'default' | 'danger' | 'success';

const VARIANT_CONFIGS: Record<
  PillVariant,
  Record<'true' | 'false', { backgroundColor: string; textColor: string; borderColor: string }>
> = {
  default: {
    true: {
      backgroundColor: 'pillSelected',
      textColor: 'inverted',
      borderColor: 'pill',
    },
    false: {
      backgroundColor: 'pillUnselected',
      textColor: 'pillUnselected',
      borderColor: 'pill',
    },
  },
  danger: {
    true: {
      backgroundColor: 'pillSelected',
      textColor: 'error',
      borderColor: 'danger',
    },
    false: {
      backgroundColor: 'pillUnselected',
      textColor: 'error',
      borderColor: 'default',
    },
  },
  success: {
    true: {
      backgroundColor: 'successDisabled',
      textColor: 'success',
      borderColor: 'success',
    },
    false: {
      backgroundColor: 'pillUnselected',
      textColor: 'success',
      borderColor: 'success',
    },
  },
};

export type PillProps = {
  id?: string;
  text: string;
  rightIcons?: string[];
  outlined?: boolean;
  onClick?: (event?: React.MouseEvent) => void;
  editable?: boolean;
  handleClose?: (event?: React.MouseEvent) => void;
  isLabelOnly?: boolean;
  variant?: 'default' | 'danger' | 'success';
  size?: 'sm' | 'lg';
};

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.25rem;
`;

const StyledPill = styled.span<{
  $selected?: boolean;
  $isLabelOnly?: boolean;
  $variant?: 'default' | 'danger' | 'success';
  $size?: 'sm' | 'lg';
}>`
  display: inline-flex;
  align-items: center;
  height: ${({ $size }) => SIZE_CONFIGS[$size || 'sm'].height};
  padding: ${({ $size }) => SIZE_CONFIGS[$size || 'sm'].padding};
  border-radius: 1.5625rem;
  font-size: ${({ $size }) => SIZE_CONFIGS[$size || 'sm'].fontSize};
  font-weight: 600;
  cursor: ${({ $isLabelOnly }) => ($isLabelOnly ? 'auto' : 'pointer')};
  user-select: none;
  background-color: ${({ $selected, $variant = 'default', theme }) =>
    theme.designSystem.bgColors[VARIANT_CONFIGS[$variant][$selected ? 'true' : 'false'].backgroundColor]};
  color: ${({ $selected, $variant = 'default', $isLabelOnly, theme }) => {
    if ($isLabelOnly && $variant === 'default') {
      return theme.designSystem.textColors.default;
    }
    return theme.designSystem.textColors[VARIANT_CONFIGS[$variant][$selected ? 'true' : 'false'].textColor];
  }};
  border: 1px solid
    ${({ $selected, $variant = 'default', $isLabelOnly, theme }) => {
      if ($isLabelOnly && $variant === 'default') {
        return theme.designSystem.borderColors.default;
      }
      return theme.designSystem.borderColors[VARIANT_CONFIGS[$variant][$selected ? 'true' : 'false'].borderColor];
    }};
  pointer-events: ${({ $isLabelOnly }) => ($isLabelOnly ? 'none' : 'auto')};
`;

const validateVariant = (variantProp?: PillVariant): PillVariant => {
  if (variantProp === 'danger' || variantProp === 'default' || variantProp === 'success') {
    return variantProp;
  }
  return 'default';
};

const Pill: FC<PillProps> = ({
  outlined,
  text,
  rightIcons,
  editable,
  handleClose,
  onClick,
  isLabelOnly = false,
  variant: variantProp,
  size = 'sm',
  ...props
}) => {
  const { selected, variant } = useMemo(() => {
    return { variant: validateVariant(variantProp), selected: !outlined };
  }, [variantProp, outlined]);

  return (
    <StyledPill
      $selected={selected}
      $isLabelOnly={isLabelOnly}
      $variant={variant}
      $size={size}
      onClick={(e) => onClick?.(e)}
      {...props}
      role={isLabelOnly ? undefined : 'button'}
      tabIndex={isLabelOnly ? undefined : 0}
      aria-pressed={isLabelOnly ? undefined : !outlined}
      data-variant={variant}
      data-outlined={!selected}
    >
      <span className="cn_line-clamp-1">{text}</span>
      {!outlined && (rightIcons || editable) && (
        <IconWrapper>
          {Array.isArray(rightIcons) &&
            rightIcons.map((icon) => <Icon name={icon} key={icon} inheritColor size="ssm" />)}
          {editable && (
            <Icon
              name="close"
              onClick={(e) => {
                e?.stopPropagation();
                handleClose?.(e);
              }}
              inheritColor
              size="ssm"
            />
          )}
        </IconWrapper>
      )}
    </StyledPill>
  );
};

export default Pill;
